import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Ticket, ArrowRight } from "lucide-react";
import { useAktiveAktion } from "@/lib/useAktion";
import { isEmbed } from "@/components/tickets/Embed";

/* Schmaler, wegklickbarer Hinweisbalken ganz oben: Tickets & Einlass per QR.
   Liegt – wie das Black-Weeks-Banner – im normalen Fluss und schiebt die fixe Navi über --bw-push nach unten.
   Teilt sich STYLE_ID/Variable mit dem Aktionsbanner und zeigt sich nur, wenn KEINE Aktion läuft → nie doppelter Offset. */

const KEY = "dimedial-ticketbanner-zu";
const STYLE_ID = "bw-nav-offset";

function useHeaderOffset(ref, active, resetOnInactive) {
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!active) { if (resetOnInactive) root.style.setProperty("--bw-push", "0px"); return; }
    let style = document.getElementById(STYLE_ID);
    if (!style) {
      style = document.createElement("style");
      style.id = STYLE_ID;
      style.textContent = `header.site-nav,[data-testid="portal-topbar"]{top:var(--bw-push,0px)!important;transition:top .12s linear}`;
      document.head.appendChild(style);
    }
    const update = () => {
      const h = ref.current ? ref.current.offsetHeight : 0;
      root.style.setProperty("--bw-push", `${Math.max(0, h - window.scrollY)}px`);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const ro = ref.current ? new ResizeObserver(update) : null;
    if (ro && ref.current) ro.observe(ref.current);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      if (ro) ro.disconnect();
      if (resetOnInactive) root.style.setProperty("--bw-push", "0px");
    };
  }, [ref, active, resetOnInactive]);
}

export const TicketBanner = () => {
  const { pathname, search } = useLocation();
  const { aktion } = useAktiveAktion();
  const [zu, setZu] = useState(true); // erst nach localStorage-Check zeigen (kein Flackern / kein Prerender)
  const ref = useRef(null);

  useEffect(() => {
    try { setZu(window.localStorage.getItem(KEY) === "1"); } catch { setZu(false); }
  }, []);

  // Werbe-Hinweis ausschließlich auf der DIMEDIAL-Startseite (/). Nie auf Kundenseiten
  // (Mini-Websites /@handle), Ticketshops, Portal/Admin oder in eingebetteten (embed/iframe) Ansichten.
  const show = !zu && !aktion && pathname === "/" && !isEmbed(search);
  useHeaderOffset(ref, show, !aktion);

  if (!show) return null;

  const dismiss = () => { try { window.localStorage.setItem(KEY, "1"); } catch { /* ignore */ } setZu(true); };

  return (
    <div ref={ref} className="relative w-full border-b border-[#E5E5E5] bg-[#F2F0EB] text-[#111]" data-testid="ticket-banner">
      <Link to="/tickets" data-testid="ticket-banner-link"
        className="flex items-center justify-center gap-2 px-10 py-2 text-center text-[12.5px] font-semibold leading-snug transition-colors hover:bg-[#ECE9E2] sm:text-[13px]"
        style={{ fontFamily: "'Poppins', sans-serif" }}>
        <Ticket size={15} className="shrink-0 text-[#111]" aria-hidden="true" />
        <span>Neu: <span className="font-bold">Tickets &amp; Einlass per QR</span> für Vereine und Events</span>
        <ArrowRight size={15} className="shrink-0" aria-hidden="true" />
      </Link>
      <button type="button" onClick={dismiss} aria-label="Hinweis schließen" data-testid="ticket-banner-close"
        className="absolute right-2 top-1/2 grid h-7 w-7 -translate-y-1/2 place-items-center rounded-full text-[#111]/50 transition-colors hover:bg-black/5 hover:text-[#111]">
        <X size={16} />
      </button>
    </div>
  );
};

export default TicketBanner;
